/**
 * App version / force-update helper.
 *
 * On native (Capacitor iOS/Android) this reads the installed app's
 * versionCode via `@capacitor/app` and hits the public
 * `/api/app-version/check` endpoint so the Super Admin can decide
 * whether to force an update.
 *
 * On web it's a strict no-op — `checkAppVersion()` returns `null`.
 * The Super Admin toggle only applies to native shells because the
 * web build auto-updates on refresh.
 */
import { Capacitor } from "@capacitor/core";
import { api } from "./api";

export async function checkAppVersion() {
  if (!Capacitor.isNativePlatform()) return null;
  let platform = "";
  let versionCode = 0;
  let versionName = "";
  try {
    const { App } = await import("@capacitor/app");
    platform = Capacitor.getPlatform(); // "ios" | "android"
    const info = await App.getInfo();
    // `build` is Android versionCode / iOS CFBundleVersion (integer as string).
    versionCode = parseInt(info?.build || "0", 10) || 0;
    versionName = info?.version || "";
  } catch (e) {
    // Plugin missing or bridge not ready — treat as "no info" so we
    // skip the check rather than block the user with a spinner.
    return null;
  }
  if (!platform || (platform !== "ios" && platform !== "android")) return null;

  try {
    const { data } = await api.get("/app-version/check", {
      params: {
        platform,
        version_code: versionCode,
        version_name: versionName,
      },
    });
    return {
      ...data,
      // Passthrough the resolved client info so callers can display it.
      client_platform: platform,
      client_version_code: versionCode,
      client_version_name: versionName,
    };
  } catch (_) {
    return null;
  }
}
